import React, {
  useState,
  useLayoutEffect,
  useRef,
  useCallback,
  useMemo,
  useEffect,
} from 'react';
import { Link } from 'react-router-dom';
import ProfileDialog from '../ProfileDialog/ProfileDialog';
import ToastNotification from '../Notification/toast';
import SideMenu from '../SideMenu/SideMenu';
import Button from '../Button/Button';
import { useRouter, useOutsideHandle } from '../../hooks';
import { useDispatch, useSelector } from 'react-redux';
import CSSTransition from 'react-transition-group/CSSTransition';
import { getInitial } from '../../utils';
import ShareGameModal from '../StreamerDashboard/ShareModal';
import ClipsUploadModal from '../Clips/ClipsUploadModal';

import logo from '../../assets/logo.svg';
import icSearch from '../../assets/ic-search.svg';
import icProfile from '../../assets/ic-profile.svg';
import icArrowBack from '../../assets/ic-arrow-back.svg';
import icMoviesMenu from '../../assets/ic-movie.svg';
import icSeriesMenu from '../../assets/ic-series.svg';
import icAnimationMenu from '../../assets/ic-animation.svg';
import icLiveTVMenu from '../../assets/ic-live-tv.svg';
import icSideMenu from '../../assets/ic-side-menu.svg';
import icClose from '../../assets/ic-close.svg';
import icCameraAdd from '../../assets/ic-camera-add.svg';
import icCart from '../../assets/ic-cart.svg';
import icBroadcast from '../../assets/ic-broadcast.svg';
import icShare from '../../assets/ic-share.svg';
import icUpload from '../../assets/ic-upload.svg';

import styles from './Header.module.css';
import { broadcasting } from '../../redux/actions/liveGame';
import PopModalDialog from '../PopModalDialog/PopModalDialog';
import LoginDialog from '../LoginDialog/LoginDialog';
import GeneralNotifModal from '../GeneralModal/generalNotifModal';
import { authPopup } from '../../redux/actions/auth';
import { callHeartbeat } from '../../redux/actions/heartbeat';
import ValidateSessionNotification from '../Notification/ValidateSessionNotification';

const pageOrderCondition = ['movies', 'series', 'animation'];
const sortPage = (page1, page2) => {
  for (let i = 0; i < pageOrderCondition.length; i++) {
    const item = pageOrderCondition[i];
    if (page1 === item) return -1;
    if (page2 === item) return 1;
    continue;
  }
};

const subMenuIcon = [icMoviesMenu, icSeriesMenu, icAnimationMenu];

const mainMenu = [
  {
    name: 'Home',
    path: '/',
    type: 'menu',
  },
  {
    type: 'submenu',
  },
  {
    name: 'Live',
    path: '/live',
    type: 'menu',
  },
  {
    name: 'Clips',
    path: '/clips',
    type: 'menu',
  },
];

const Header = ({ showHeaderMenu }) => {
  const dispatch = useDispatch();
  const { user, authPopupStatus, token, sessionStatus } = useSelector(
    (state) => state.auth
  );
  const pageData = useSelector((state) => state.page.data);
  const { liveGameInfo } = useSelector((state) => state.liveGame);
  const authenticated = !!user;
  const [showProfile, toggleDialog] = useState(false);
  const [showSideMenu, toggleSideMenu] = useState(false);
  const [showSearchField, toggleSearch] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [endStreamModal, setEndStreamModal] = useState(false);
  const [showLiveAndClipsMenu, setShowLiveAndClipsMenu] = useState(false);
  const [showClipsUploadModal, setShowClipsUploadModal] = useState(false);
  const headerRef = useRef();
  const dialogRef = useRef();
  const formRef = useRef();
  const searchRef = useRef();
  const liveRef = useRef();
  const [intervalId, setIntervalId] = useState();

  const toggleProfile = () => {
    toggleDialog((open) => !open);
  };
  const router = useRouter();
  const history = router.history;
  const isStreamerPage = router.location.pathname === '/streamer-dashboard';
  const subMenu = useMemo(() => {
    const subMenuList = pageData
      .filter((item) => item.friendlyId !== 'home')
      .sort((firstPage, secondPage) => {
        const { friendlyId: firstId } = firstPage;
        const { friendlyId: secondId } = secondPage;
        const sortValue = sortPage(firstId, secondId);
        return sortValue || 0;
      })
      .map((item, i) => {
        const name = item.titleLocalized;
        const friendlyId = item.friendlyId;
        return {
          name,
          path: `/${friendlyId}`,
          icon: subMenuIcon[i],
        };
      });

    subMenuList &&
      subMenuList.push({
        name: 'Live TV',
        path: '/live-tv',
        icon: icLiveTVMenu,
      });

    return subMenuList;
  }, [pageData]);
  const newMainMenuData = useMemo(() => {
    if (subMenu && subMenu.length) {
      return mainMenu.map((item) => {
        if (item.type === 'submenu') {
          item.subMenuItem = subMenu;
        }
        return item;
      });
    }
  }, [subMenu]);
  const activeSubMenu = useMemo(() => {
    if (subMenu && subMenu.length) {
      const active = subMenu.find(
        (item) => item.path === router.location.pathname
      );

      return active || subMenu[0];
    }
  }, [subMenu, router.location.pathname]);
  const [selectedSubMenu, setSelectedSubMenu] = useState(activeSubMenu);

  useEffect(() => {
    setSelectedSubMenu(activeSubMenu || selectedSubMenu);
  }, [activeSubMenu, selectedSubMenu]);

  const handleClickOutsideDialog = useCallback(
    (target) => {
      //ignore user-profile-icon to use its onClick func
      if (target.dataset.iconName === 'user-profile-icon') {
        return;
      }
      if (showProfile) {
        toggleProfile();
      }
    },
    [showProfile]
  );

  const handleClickOutsideSearchField = useCallback(() => {
    if (showSearchField) {
      toggleSearch(!showSearchField);
    }
  }, [showSearchField]);

  const handleClickOutsideLiveAndClipsMenu = useCallback(
    (target) => {
      if (target.dataset.button === 'live-and-clips-button') return;
      if (showLiveAndClipsMenu) {
        setShowLiveAndClipsMenu(false);
      }
    },
    [showLiveAndClipsMenu]
  );

  useOutsideHandle(dialogRef, handleClickOutsideDialog);
  useOutsideHandle(formRef, handleClickOutsideSearchField);
  useOutsideHandle(liveRef, handleClickOutsideLiveAndClipsMenu);

  useLayoutEffect(() => {
    if (showSearchField) {
      searchRef.current.focus();
    }
  }, [showSearchField]);

  // const handleClickSearch = () => {
  //   toggleSearch(!showSearchField)
  // }

  const handleSubmitSearch = (e) => {
    e.preventDefault();
    if (searchRef.current.value) {
      history.push(`/search?keyword=${searchRef.current.value}`);
    }
  };

  const handleGoLive = () => {
    if (authenticated) {
      setShowLiveAndClipsMenu(false);
      return history.push('/streamer-dashboard');
    }
    return dispatch(authPopup.show());
  };

  const toggleBodyScroll = (action) => {
    const body = document.querySelector('body');
    body.style.overflow = action;
  };

  const handleUploadClips = () => {
    if (authenticated) {
      setShowLiveAndClipsMenu(false);
      toggleBodyScroll('hidden');
      return setShowClipsUploadModal(true);
    }
    return dispatch(authPopup.show());
  };

  const handleStreamButton = () => {
    if (liveGameInfo.isBroadcasting) {
      setEndStreamModal(true);
    } else {
      dispatch(broadcasting());
      dispatch(callHeartbeat(liveGameInfo?.id));
      let callHeartBeat = setInterval(() => {
        dispatch(callHeartbeat(liveGameInfo?.id));
      }, 60000);
      setIntervalId(callHeartBeat);
    }
  };

  return (
    <div className={styles.headerSpace}>
      <div className={styles.header} ref={headerRef}>
        <ToastNotification />
        <SideMenu open={showSideMenu} menu={newMainMenuData} />
        <img
          className={`${styles['side-menu-toggle']} show-mobile`}
          src={showSideMenu ? icClose : icSideMenu}
          alt='side-menu-toggle'
          onClick={() => toggleSideMenu(!showSideMenu)}
        />
        <Link to='/'>
          <img src={logo} alt='logo' />
        </Link>
        {showHeaderMenu ? (
          <ul className={`${styles['menu']} hide-mobile`}>
            {newMainMenuData.map((item) => {
              const selected = router.location.pathname === item.path;

              if (
                item.type === 'submenu' &&
                item.subMenuItem.length &&
                activeSubMenu
              ) {
                const selected =
                  router.location.pathname === activeSubMenu.path;

                return (
                  <li
                    key={activeSubMenu.name}
                    className={selected ? styles['selected'] : ''}
                  >
                    <PopModalDialog
                      content={
                        <div className={styles['sub-menu-wrapper']}>
                          {item.subMenuItem.map((item) => {
                            return (
                              <Button
                                key={item.name}
                                icon={item.icon}
                                type='text'
                                style={{
                                  margin: '0 10px',
                                  fontSize: 14,
                                }}
                                onClick={() => router.history.push(item.path)}
                              >
                                {item.name}
                              </Button>
                            );
                          })}
                        </div>
                      }
                      action='hover'
                      modalStyle={{
                        borderRadius: 50,
                      }}
                      customYBase={headerRef.current}
                    >
                      <span>
                        {activeSubMenu.name}
                        <img src={icArrowBack} alt='drop-down' data-dropdown />
                      </span>
                    </PopModalDialog>
                  </li>
                );
              }

              return (
                <li
                  key={item.name}
                  className={selected ? styles['selected'] : ''}
                >
                  <Link to={item.path}>{item.name}</Link>
                </li>
              );
            })}
          </ul>
        ) : (
          <div className={styles['streamer-dashboard-header']}>
            <img src={icBroadcast} alt='signal' />
            Streamer Dashboard
          </div>
        )}

        <ul className={styles['actions-menu']}>
          {showHeaderMenu && (
            <>
              <li className={`${styles.search} ${styles['search-opened']}`}>
                <form
                  className={styles['search-form']}
                  ref={formRef}
                  onSubmit={handleSubmitSearch}
                >
                  <i
                    className={styles['search-media-icon']}
                    onClick={handleSubmitSearch}
                  />
                  <input
                    ref={searchRef}
                    placeholder='Search movies, live, clips'
                    className={styles['search-media-field']}
                  />
                </form>
              </li>
              <li className={styles['search-button-modal']}>
                <PopModalDialog
                  content={
                    <form
                      className={styles['search-form']}
                      ref={formRef}
                      onSubmit={handleSubmitSearch}
                    >
                      <i
                        className={styles['search-media-icon']}
                        onClick={handleSubmitSearch}
                      />
                      <input
                        ref={searchRef}
                        placeholder='Search movies, live, clips'
                        className={styles['search-media-field']}
                      />
                    </form>
                  }
                  modalStyle={{
                    borderRadius: 50,
                  }}
                  customYBase={headerRef.current}
                >
                  <img src={icSearch} alt='search' />
                </PopModalDialog>
              </li>
              <li className={styles['subscribe-button']}>
                <Button
                  icon={icCart}
                  iconSize={20}
                  style={{
                    margin: 0,
                    fontSize: 16,
                    background:
                      'linear-gradient(104.77deg, #FF3434 -25.37%, #FF7D34 100%)',
                  }}
                  shape='rounded'
                  onClick={() =>
                    token ? history.push('/shop') : dispatch(authPopup.show())
                  }
                >
                  Subscribe / Topup
                </Button>
              </li>
              <li className={styles['live-button']}>
                <PopModalDialog
                  spacing={30}
                  content={
                    <div className={styles['popup-menu']}>
                      <div onClick={handleGoLive}>
                        <img src={icBroadcast} alt='signal' width={23} />
                        <p>Go Live</p>
                      </div>
                      <div onClick={handleUploadClips}>
                        <img src={icUpload} alt='upload' width={18} />
                        <p>Upload Clips</p>
                      </div>
                    </div>
                  }
                  customYBase={headerRef.current}
                >
                  <Button
                    icon={icCameraAdd}
                    iconSize={20}
                    type='text'
                    style={{
                      margin: 0,
                      fontSize: 16,
                    }}
                    data-button='live-and-clips-button'
                  >
                    Create
                  </Button>
                </PopModalDialog>
              </li>
            </>
          )}
          {authenticated ? (
            <>
              {liveGameInfo && isStreamerPage && (
                <>
                  <li>
                    <div
                      className={
                        liveGameInfo.isBroadcasting
                          ? styles['stop-streaming']
                          : styles['start-streaming']
                      }
                      onClick={handleStreamButton}
                    >
                      {liveGameInfo.isBroadcasting
                        ? 'End Stream'
                        : 'Start Streaming'}
                    </div>
                  </li>
                  <li>
                    <img
                      src={icShare}
                      alt='share'
                      onClick={() => setShowShareModal(!showShareModal)}
                    />
                  </li>
                </>
              )}
              <li>
                <PopModalDialog
                  placement='bottom-right'
                  spacing={30}
                  content={<ProfileDialog />}
                  customYBase={headerRef.current}
                >
                  {user && user.profile && user.profile.avatar ? (
                    <img
                      src={user.profile.avatar}
                      className={styles['user-profile-icon']}
                      alt='icon'
                      data-icon-name='user-profile-icon'
                      // onClick={toggleProfile}
                    />
                  ) : (
                    <div
                      className={styles['user-profile-icon']}
                      data-initial-name
                    >
                      {getInitial(user.profile.name)}
                    </div>
                  )}
                </PopModalDialog>
              </li>
            </>
          ) : (
            <>
              <li>
                <img
                  src={
                    user && user.profile && user.profile.avatar
                      ? user.profile.avatar
                      : icProfile
                  }
                  className={styles['user-profile-icon']}
                  alt='icon'
                  data-icon-name='user-profile-icon'
                  onClick={() => dispatch(authPopup.show())}
                />
              </li>
            </>
          )}
        </ul>

        {showShareModal && (
          <ShareGameModal
            onCloseModal={() => setShowShareModal(!showShareModal)}
          />
        )}

        {endStreamModal && (
          <div className={styles.modalWrapper}>
            <div className={styles.endStreamModal}>
              <p>Do you want to end the stream?</p>
              <Button
                width='120px'
                bgColor='#282828'
                onClick={() => {
                  setEndStreamModal(false);
                }}
              >
                No
              </Button>
              <Button
                width='120px'
                onClick={async () => {
                  await dispatch(broadcasting());
                  await clearInterval(intervalId);
                  await history.push('/live');
                  setEndStreamModal(false);
                }}
              >
                Yes
              </Button>
            </div>
          </div>
        )}

        <GeneralNotifModal open={authPopupStatus}>
          <LoginDialog onClose={() => dispatch(authPopup.close())} />
        </GeneralNotifModal>

        <CSSTransition
          in={showClipsUploadModal}
          timeout={100}
          classNames='fade'
          unmountOnExit
        >
          <ClipsUploadModal
            closeModal={() => {
              setShowClipsUploadModal(false);
              toggleBodyScroll('auto');
            }}
          />
        </CSSTransition>

        <ValidateSessionNotification
          open={
            sessionStatus?.status === 'failed' && sessionStatus.attempts >= 3
          }
        />
      </div>
    </div>
  );
};

export default Header;
